import { useSession } from "@/session/useSession";
import { Suspense } from "react";
import { useRoute, type Route } from "@/router";
import { MissionShell } from "@/shell/MissionShell";
import { Island } from "@/shell/island";
import { RouteLifecycle } from "@/shell/RouteLifecycle";
import { matchView } from "@/shell/views";
import { SignIn } from "@/views/SignIn";
import { Today } from "@/views/Today";
import { Admin } from "@/views/Admin";
import { NotFound } from "@/views/NotFound";
import { RequisitionPage, RequisitionsList } from "@/views/Requisitions";
import { IntakePage } from "@/views/Intake";
import { AccessPage } from "@/views/Access";
import { ConcernsPage } from "@/views/Concerns";
import { PhaseTargets } from "@/views/PhaseTargets";
import { ApplicationPage } from "@/views/Candidates";
import { RoundPage } from "@/views/Rounds";
import { PacketPage, PacketsHome, ScorecardPage } from "@/views/Packets";
import { PacketSetPage } from "@/views/PacketSet";
import { DebriefRoom, DebriefsHome, SummarySheetPage } from "@/views/Debriefs";
import { ProposalPage, ProposalsHome } from "@/views/Proposals";
import { Guide } from "@/views/Guide";
import { Info } from "@/views/Info";
import { Faq } from "@/views/Faq";

// The navigation is server data (`shell.nav`, plan D17): the union of the DC-32 seat sets the person holds, each item
// shown only when its gate tool is callable — the decision stays the manifest's and the server's, never a role table in
// TypeScript (V1-INTERACTIONS §1.3, standards §3.5).
//
// V1's screens stay reachable at their routes whether or not the nav lists them (Today, Drafts, Phase targets and Admin
// are hosted, unlisted; Process Guide, Interview Guidance and FAQ sit behind the ? menu). Each keeps the manifest tool
// its landing route reads, so N02 holds for every one of them: the bare route is the §3.3 not-found screen for a seat
// whose manifest does not carry it. Lanes' screens carry their own gates on their registerView line (shell/views.ts).
const V1_GATES: Readonly<Record<string, readonly string[]>> = {
  requests: ["request.list_mine", "request.queue_hrbp", "request.queue_ta", "request.list"],
  requisitions: ["requisition.list"],
  candidates: ["candidate.list"],
  packets: ["packet.list_mine"],
  debriefs: ["debrief.list_mine"],
  proposals: ["proposal.open"],
  "phase-targets": ["requisition.list"],
  info: ["guide.reference"],
  guide: ["guide.open"],
  faq: ["guide.faq"],
  admin: ["feedback.list", "config.list"],
};

/** The manifest tools behind a route's landing screen: a lane's registered gates, else V1's, else none. */
function landingGates(route: Route): readonly string[] {
  return matchView(route)?.gates ?? V1_GATES[route.view] ?? [];
}

export function App() {
  return <><RouteLifecycle /><Screens /></>;
}

function Screens() {
  const { me, loading, can } = useSession();
  const route = useRoute();

  if (loading) return <div className="signin">Loading…</div>;
  if (!me) return <SignIn />;

  // N02 has two halves. The nav shows no item whose gating tool is absent from the manifest — and the LANDING route
  // behind that item refuses the same way, rather than rendering its screen over a list call that will be turned down.
  // Typed into the address bar, kept in a bookmark or followed from an old mail, `#requisitions` is the §3.3
  // not-found screen for a seat whose manifest does not carry `requisition.list` (AF-1). A record UNDER a landing route
  // is NOT gated here: it carries its own seat rule and its own refusal, and some of those records belong to seats the
  // list tool does not — an interviewer's debrief room is `#requisitions/<R>/debriefs/<dc>` and must stay reachable
  // (§4.15).
  const gates = landingGates(route);
  if (route.parts.length === 0 && gates.length > 0 && !gates.some(can)) {
    return <MissionShell><NotFound route={route.raw} /></MissionShell>;
  }

  // A lane's registered screen (B1's Home and Action Center, every later lane's) before V1's own cases. It is a V2
  // screen, so it renders inside its own `.mc` island; V1's screens below never do (plan D12). V1 parts a V2 screen still
  // draws (the not-found screen, a read's refusal, the V1 requisition controls) go through V1Slot, outside the island.
  const registered = matchView(route);
  if (registered) {
    const View = registered.component;
    return <MissionShell><Island><Suspense fallback={null}><View route={route} /></Suspense></Island></MissionShell>;
  }

  let body;
  switch (route.view) {
    case "today":
      body = <Today />;
      break;
    case "admin":
      body = <Admin tab={route.parts[0]} configKey={route.parts[1] ? decodeURIComponent(route.parts[1]) : undefined} version={route.parts[2]} />;
      break;
    case "phase-targets":
      body = <PhaseTargets />;
      break;
    case "guide":
      body = <Guide />;
      break;
    case "info":
      body = <Info />;
      break;
    case "faq":
      body = <Faq />;
      break;
    case "debriefs":
      body = <DebriefsHome />;
      break;
    case "proposals":
      body = route.parts[0] === undefined ? <ProposalsHome /> : <ProposalPage key={route.parts[0].split("?")[0]} id={route.parts[0].split("?")[0]!} />;
      break;
    case "packets":
      body = route.parts[0] === undefined ? <PacketsHome /> : route.parts[1] ? <ScorecardPage key={`${route.parts[0].split("?")[0]}/${route.parts[1].split("?")[0]}`} pk={route.parts[0].split("?")[0]!} cand={route.parts[1].split("?")[0]!} /> : <PacketPage key={route.parts[0].split("?")[0]} pk={route.parts[0].split("?")[0]!} />;
      break;
    case "requisitions":
      body =
        route.parts[0] === undefined ? (
          <RequisitionsList />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "access" ? (
          <AccessPage id={route.parts[0].split("?")[0]!} askNow={(route.parts[1] ?? "").includes("request=1")} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "concerns" ? (
          <ConcernsPage rid={route.parts[0].split("?")[0]!} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "debriefs" && route.parts[2] ? (
          // the room and the sheet remount on their complete record identity — requisition × cycle, requisition ×
          // application × cycle — so no roll selection, slot, dialog or rendition survives a move to another record (M11)
          <DebriefRoom key={`${route.parts[0].split("?")[0]}/${route.parts[2].split("?")[0]}`} rid={route.parts[0].split("?")[0]!} cid={route.parts[2].split("?")[0]!} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "summary" && route.parts[2] ? (
          <SummarySheetPage key={`${route.parts[0].split("?")[0]}/${route.parts[2].split("?")[0]}/${route.parts[3]?.split("?")[0] ?? ""}`} rid={route.parts[0].split("?")[0]!} aid={route.parts[2].split("?")[0]!} cid={route.parts[3]?.split("?")[0]} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "packets" ? (
          <PacketSetPage id={route.parts[0].split("?")[0]!} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "candidates" && route.parts[2] && route.parts[3] === "rounds" && route.parts[4] ? (
          <RoundPage key={`${route.parts[0]}/${route.parts[2]}/${route.parts[4]}`} rid={route.parts[0].split("?")[0]!} aid={route.parts[2]} n={route.parts[4].split("?")[0]!} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "candidates" && route.parts[2] ? (
          <ApplicationPage key={`${route.parts[0].split("?")[0]}/${route.parts[2].split("?")[0]}`} rid={route.parts[0].split("?")[0]!} aid={route.parts[2].split("?")[0]!} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") && route.parts[1]?.split("?")[0] === "intake" ? (
          <IntakePage key={route.parts[0].split("?")[0]} id={route.parts[0].split("?")[0]!} part={route.parts[2]?.split("?")[0] ?? ""} />
        ) : /^R-\d+$/.test(route.parts[0].split("?")[0] ?? "") ? (
          <RequisitionPage key={route.parts[0].split("?")[0]} id={route.parts[0].split("?")[0]!} tab={route.parts[1]?.split("?")[0] ?? ""} />
        ) : (
          <NotFound route={route.raw} />
        );
      break;
    default:
      body = <NotFound route={route.raw} />;
  }
  return <MissionShell>{body}</MissionShell>;
}
